import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import OrganizationLevelDetail from "./OrganizationLevelDetail";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function OrganizationLevel() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  // if (!organizationId || organizationId === "null") {
  //   return;
  // }

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      levels: [
        {
          id: 0,
          name: "",
          comments: "",
          orderNo: 1,
          createdBy: localStorage.getItem("userId"),
          organizationId: organizationId,
          accountLength: localStorage.getItem("accountLength"),
        },
      ],
    },
  });

  const [centersData, setCentersData] = useState([]);
  const [organizationLevels, setOrganizationLevels] = useState([]);


  useEffect(() => {
    getCentersList();
    const fetchOrganizationLevels = async () => {
      try {
        const response = await get(
          `OrganizationLevel/GetOrganizationLevelsByOrganization?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setOrganizationLevels(response.levels);
      } catch (error) {
        console.error("Error fetching GetOrganizationLevels:", error);
      }
    };

    fetchOrganizationLevels();
  }, [triggerRerender]);

  const getCentersList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Center/GetCenterByOrganizationId?organizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCentersData(response.center);
    } catch (error) {
      console.error("Error fetching Centers:", error);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1, // Simple row number based on current page index
      },
      {
        accessorKey: "name",
        header: "Name",
        // size: 150,
      },
      {
        accessorKey: "comments",
        header: "Comments",
        // size: 150,
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEditClick(row.original.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDeleteClick(row.original.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );
  const exportToExcel = () => {
    const filteredData = organizationLevels.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      Comments: row.comments,
      // "Order No": row.orderNo,
      //ID: row.id,
    }));

    console.log(filteredData);
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "OrganizationLevels");
    XLSX.writeFile(workbook, "OrganizationLevels.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleId == "2" ? orgLogo : roleId == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Organization Levels";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions"
    );

    const headers = includedColumns.map((col) => col.header);

    const bodyData = organizationLevels.map((row, index) => {
      return includedColumns.map((column) => {
        if (column.accessorKey === "mrt-row-numbers") return index + 1;
        return row[column.accessorKey] || "";
      });
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("OrganizationLevels.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  // const exportToPDF = () => {
  //   const filteredData = organizationLevels.map((item) => {
  //     const { Action, ...rest } = item; // Exclude the 'Action' column
  //     return rest;
  //   });
  //   console.log("Exporting to PDF", filteredData);
  //   const doc = new jsPDF();
  //   const tableColumn = columns.map((col) => col.name);
  //   const tableRows = filteredData.map((row) =>
  //     columns.map((col) => (col.selector ? col.selector(row) : ""))
  //   );

  //   doc.autoTable({
  //     head: [tableColumn],
  //     body: tableRows,
  //   });
  //   doc.save("Organizations.pdf");
  // };

  const handleEditClick = async (rowId) => {
    const singleLevelData = await get(
      `OrganizationLevel/GetOrganizationLevelsById?levelId=${rowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const singleLevelDataLevels = singleLevelData.levels;

    reset({
      levels: [
        {
          id: singleLevelDataLevels.id || 0,
          name: singleLevelDataLevels.name || "",
          comments: singleLevelDataLevels.comments || "",
          orderNo: singleLevelDataLevels.orderNo || 1,
          organizationId:
            singleLevelDataLevels.organizationId || organizationId,
          accountLength:
            singleLevelDataLevels.accountLength ||
            localStorage.getItem("accountLength"),
          createdBy:
            singleLevelDataLevels.createdBy || localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
        },
      ],
    });
  };

  const handleDeleteClick = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this level?"
    );

    if (confirmDelete) {
      const formData = {
        id: rowId,
        isDelete: true,
        deletedBy: localStorage.getItem("userId"),
      };

      try {
        const response = await post(
          `OrganizationLevel/DeleteOrganizationLevelsById`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.isSuccess) {
          toast.success(response.message || "Success!");
          setTriggerRerender((prev) => !prev);
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "levels",
  });

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (centersData.length > 0 && data.levels.some((level) => level.id === 0)) {
      toast.error("Cannot add new levels when centers exist.");
      return;
    }

    const formData = data.levels.map((level) => ({
      ...level,
      // id: level.name.value || 0,
      // name: level.name.label || level.name,
    }));

    try {
      const response = await post(
        "OrganizationLevel/UpdateOrganizationLevelsAsync",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success("Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          levels: [
            {
              id: 0,
              name: "",
              comments: "",
              orderNo: 1,
              createdBy: localStorage.getItem("userId"),
              organizationId: organizationId,
              accountLength: localStorage.getItem("accountLength"),
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };



  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Organization Levels</h2>
        {/* <div className="ms-auto">
          <Link
            to="/organization-form"
            className="btn btn-outline-primary btn-sm"
          >
            Add New
          </Link>
        </div> */}
      </div>

      <form className="row g-3" onSubmit={handleSubmit(saveData)}>
        <div className="col-6">
          <div className="card">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-4">
                  <label className="form-label">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                </div>
                <div className="col">
                  <label className="form-label">
                    Order<span className="text-danger fs-9">*</span>
                  </label>
                </div>
                <div className="col-4">
                  <label className="form-label">Comments</label>
                </div>
                <div className="col-2">
                  <label className="form-label">Action</label>
                </div>
              </div>
              {fields.map((field, index) => (
                <div className="row g-2 mb-2" key={field.id}>
                  <div className="col-4">
                    <input
                      {...register(`levels[${index}].name`, {
                        required: "This field is required.",
                        maxLength: {
                          value: 75,
                          message: "Maximum of 75 characters allowed.",
                        },
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck(
                        `levels[${index}].name`
                      )}
                    />
                    {errors.levels?.[index]?.name && (
                      <small className="text-danger">
                        {errors.levels[index].name.message}
                      </small>
                    )}
                  </div>
                  <div className="col">
                    <input
                      {...register(`levels[${index}].orderNo`, {
                        required: "required.",
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                    />
                    {errors.levels?.[index]?.orderNo && (
                      <small className="text-danger">
                        {errors.levels[index].orderNo.message}
                      </small>
                    )}
                  </div>
                  <div className="col-4">
                    <input
                      {...register(
                        `levels[${index}].comments`,
                        {
                          maxLength: {
                            value: 250,
                            message: "Maximum of 250 characters allowed.",
                          },
                        }
                        //   {
                        //   required: "This field is required.",
                        // }
                      )}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck(
                        `levels[${index}].comments`
                      )}
                    />
                    {errors.levels?.[index]?.comments && (
                      <small className="text-danger lh-1">
                        {errors.levels[index].comments.message}
                      </small>
                    )}
                  </div>
                  <div className="col-2">
                    <button
                      className="btn btn-outline-primary py-1 px-2 lh-2"
                      type="button"
                      onClick={() =>
                        append({
                          id: 0,
                          createdBy: localStorage.getItem("userId"),
                          organizationId: organizationId,
                          name: "",
                          comments: "",
                          orderNo: fields.length + 1,
                        })
                      }
                    >
                      <span className="fas fa-plus"></span>
                    </button>
                    {fields.length > 1 && (
                      <button
                        className="btn btn-outline-danger py-1 px-2 lh-2 ms-1"
                        type="button"
                        onClick={() => remove(index)}
                      >
                        <span className="fas fa-minus"></span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="mt-3">
                {centersData.length > 0 && watch("levels.0.id") === 0 ? (
                  <small className="text-danger fs-9">
                    Note: Adding another level is not permitted as a
                    center/campus has already been created.
                  </small>
                ) : (
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={organizationLevels}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default OrganizationLevel;
